<script setup lang="ts">
import { AUTH_FOOTER_LINK as FOOTER_LINK } from './authClasses'

/**
 * The signed-out page shell (Login, Signup, ResetPassword, LockedOut boards): a paper pane with the
 * brand row, the form column centred in the remaining height and a footer row, and a dark `aside`
 * pane beside it from 1024px (hidden below, so a phone opens straight on the form).
 *
 * - `width`: max width of the form column (Login and Reset 380, Signup 400, Locked out 420).
 * - `asideLabel`: accessible name of the dark pane ("Product preview", "What you get", …).
 * - Slots: `default` the form column, `aside` the dark pane's content (the consumer sets its own
 *   gap), `footer` replaces the footer row's content.
 */
withDefaults(
  defineProps<{
    width?: 380 | 400 | 420
    asideLabel?: string | undefined
  }>(),
  { width: 380 },
)

defineSlots<{
  default(): unknown
  aside?(): unknown
  footer?(): unknown
}>()

// Full class names so Tailwind sees them.
const WIDTH = {
  380: 'max-w-[380px]',
  400: 'max-w-[400px]',
  420: 'max-w-[420px]',
} as const
</script>

<template>
  <div class="flex min-h-screen bg-paper text-sm text-ink">
    <div class="flex min-w-0 flex-1 basis-[480px] flex-col px-[clamp(20px,6vw,88px)] py-8">
      <div class="flex items-center gap-2.5">
        <span
          class="grid h-[30px] w-[30px] flex-none place-items-center rounded-lg bg-ink font-bold text-white"
          aria-hidden="true"
        >N</span>
        <span class="text-[15px] font-semibold">NerdCRM</span>
      </div>

      <main class="flex flex-1 items-center py-12">
        <div :class="['w-full', WIDTH[width]]">
          <slot />
        </div>
      </main>

      <footer class="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-ink-3">
        <slot name="footer">
          <span>© 2026 Nerddevs</span>
          <router-link to="/help" :class="FOOTER_LINK">Help</router-link>
          <a href="/privacy.html" :class="FOOTER_LINK">Privacy</a>
          <span>
            A product of
            <a href="https://nerddevs.com" target="_blank" rel="noopener noreferrer" :class="FOOTER_LINK">Nerddevs Ltd</a>
          </span>
          <router-link to="/" :class="[FOOTER_LINK, 'no-underline sm:ml-auto']">← Back to landing page</router-link>
        </slot>
      </footer>
    </div>

    <aside
      v-if="$slots.aside"
      :aria-label="asideLabel"
      class="hidden min-w-0 flex-1 basis-[480px] flex-col justify-center bg-ink px-[clamp(20px,6vw,88px)] py-12 text-paper lg:flex"
    >
      <slot name="aside" />
    </aside>
  </div>
</template>
